import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  useVoiceEditControllerEditVoiceMutation,
  useVoiceListControllerVoiceListQuery,
  VoiceDto,
  useVoiceCreateControllerCreateVoiceMutation,
  useVoiceDeleteControllerDeleteVoiceMutation,
  ElevenLabsSettingsRequestDto,
} from '@shared/services/apiService/apiService';
import { resolveVoiceProviderDisplayName } from '@shared/utils/voiceDisplay';
import { VoiceSettingsWithName } from './types';

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
    overflowY: 'scroll',
    overflowX: 'hidden',
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    color: theme.palette.text.secondary,
  },
}));

export const ManageVoiceSettings: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    isLoading: isLoadingVoices,
    refetch: refreshVoices,
    data: voices,
  } = useVoiceListControllerVoiceListQuery();
  const [editVoice] = useVoiceEditControllerEditVoiceMutation();
  const [createVoice] = useVoiceCreateControllerCreateVoiceMutation();
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [voiceProvider, setVoiceProvider] = useState<string>('');
  const [providerDisplayName, setProviderDisplayName] = useState<string>('');
  const [voiceId, setVoiceId] = useState<string | undefined>(undefined);
  const [gender, setGender] = useState<string | undefined>(undefined);
  const [accent, setAccent] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState<VoiceDto | null>(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [selectedVoice, setSelectedVoice] = useState<VoiceDto | null>(null);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettingsWithName | null>(null);
  const [voiceSettingsString, setVoiceSettingsString] = useState<string | null>();
  const [isJsonValid, setIsJsonValid] = useState(true);

  const handleEditVoice = (voice: VoiceDto) => {
    setSelectedVoice(voice);
    setVoiceSettingsString(JSON.stringify(voice.voiceSettings, null, 2));
    setPreviewUrl(voice.previewUrl || '');
    setGender(voice.gender);
    setAccent(voice.accent);
    setProviderDisplayName(voice.providerDisplayName ?? '');

    if (voice.voiceSettings) {
      const voiceSettingsRequest = {
        ...voice.voiceSettings,
        provider: (voice.voiceSettings as any).provider || voice.provider || '',
        voiceId: (voice.voiceSettings as any).voiceId || voice.voiceId || '',
      } as any;
      setVoiceProvider(voiceSettingsRequest.provider || voice.provider || '');
      setVoiceId(voiceSettingsRequest.voiceId || voice.voiceId || '');
      setVoiceSettings({
        name: voice.name,
        voiceSettings: voiceSettingsRequest,
      });
    }

    setOpen(true);
  };

  const openDeleteDialog = (voice: VoiceDto) => {
    setVoiceToDelete(voice);
    setDeleteConfirmationInput('');
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setVoiceToDelete(null);
    setDeleteConfirmationInput('');
    setDeleteOpen(false);
  };

  const [deleteVoice] = useVoiceDeleteControllerDeleteVoiceMutation();

  const handleDeleteConfirmed = async () => {
    if (!voiceToDelete) return;
    const expected = t('superAdminManageVoice.deleteConfirmationPhrase', {
      name: voiceToDelete.name,
    });
    if (deleteConfirmationInput.trim() !== expected) return;

    try {
      await deleteVoice({ id: voiceToDelete.id }).unwrap();
      refreshVoices();
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('superAdminManageVoice.voiceRemovedTitle'),
          description: t('superAdminManageVoice.voiceRemovedDescription', {
            name: voiceToDelete.name,
          }),
        },
      });
    } catch (err: unknown) {
      const e: any = err;
      const errorMessage =
        e && e.data && typeof e.data === 'object' && 'message' in e.data
          ? e.data.message
          : e?.message || t('superAdminManageVoice.genericError');
      toast.error((props) => <CustomErrorToast {...props} message={errorMessage} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    } finally {
      closeDeleteDialog();
    }
  };

  const handleCreateVoiceClick = () => {
    setSelectedVoice(null);
    const defaultVoiceSettings: ElevenLabsSettingsRequestDto = {
      minInterruptionWords: 0,
      minInterruptionDuration: 0,
      minEndpointingDelay: 0,
      maxEndpointingDelay: 0,
      model: 'eleven_multilingual_v2',
      similarityBoost: 0.8,
      stability: 0.5,
      useSpeakerBoost: true,
      style: 2,
      language: 'en',
    };

    setVoiceSettingsString(JSON.stringify(defaultVoiceSettings, null, 2));
    setVoiceProvider('');
    setProviderDisplayName('');
    setVoiceId('');
    setVoiceSettings({ name: '', voiceSettings: defaultVoiceSettings });
    setPreviewUrl('');
    setGender(undefined);
    setAccent(undefined);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!voiceSettings) return;
    if (!voiceProvider) return;

    const voiceSettingsWithProvider = {
      ...voiceSettings.voiceSettings,
      provider: voiceProvider,
      ...(voiceId && { voiceId }),
    } as any;

    const trimmedDisplayName = providerDisplayName.trim();
    const voiceRequestDto = {
      name: voiceSettings.name,
      previewUrl: previewUrl || '',
      gender,
      accent,
      provider: voiceProvider,
      providerDisplayName: trimmedDisplayName ? trimmedDisplayName : voiceProvider,
      voiceId,
      voiceSettings: voiceSettingsWithProvider,
    } as any;

    if (selectedVoice) {
      try {
        await editVoice({ id: selectedVoice.id, voiceRequestDto }).unwrap();
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('superAdminManageVoice.voiceEditedTitle'),
            description: t('superAdminManageVoice.voiceEditedDescription', {
              name: voiceRequestDto.name,
            }),
          },
        });
      } catch (err: unknown) {
        const e: any = err;
        const errorMessage =
          e && e.data && typeof e.data === 'object' && 'message' in e.data
            ? e.data.message
            : e?.message || t('superAdminManageVoice.genericError');
        toast.error((props) => <CustomErrorToast {...props} message={errorMessage} />, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        });
        return;
      }
    } else {
      try {
        await createVoice({ voiceRequestDto }).unwrap();
        // show created toast
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('superAdminManageVoice.voiceCreatedTitle'),
            description: t('superAdminManageVoice.voiceCreatedDescription', {
              name: voiceRequestDto.name,
            }),
          },
        });
      } catch (err: unknown) {
        const e: any = err;
        const errorMessage =
          e && e.data && typeof e.data === 'object' && 'message' in e.data
            ? e.data.message
            : e?.message || t('superAdminManageVoice.genericError');
        toast.error((props) => <CustomErrorToast {...props} message={errorMessage} />, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        });
        return;
      }
    }

    refreshVoices();
    setOpen(false);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    try {
      setVoiceSettingsString(value);

      const parsed = JSON.parse(value);
      if (parsed.provider) {
        setVoiceProvider(parsed.provider);
      }
      if (parsed.voiceId) {
        setVoiceId(parsed.voiceId);
      }
      setVoiceSettings((voiceSettings) => {
        return voiceSettings ? { ...voiceSettings, voiceSettings: parsed } : null;
      });
      setIsJsonValid(true);
    } catch {
      setIsJsonValid(false);
    }
  };

  if (isLoadingVoices) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (voices && voices.length > 0) {
    return (
      <>
        <Box
          sx={{
            height: '50px !important',
            display: 'flex',
            justifyContent: 'flex-end',
            my: 2,
            alignItems: 'center',
          }}>
          <Button variant="contained" color="primary" onClick={handleCreateVoiceClick}>
            {t('superAdminManageVoice.createVoice')}
          </Button>
        </Box>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.name')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.gender')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.accent')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.providerDisplay')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.preview')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voices.map((voice) => (
                <TableRow key={voice.id} className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.name}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.gender} {voice.gender === 'male' ? '👨' : '👩'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.accent}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {resolveVoiceProviderDisplayName(voice)}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <audio controls>
                      <source src={voice.previewUrl} type="audio/mpeg" />
                      {t('superAdminManageVoice.audioFallback')}
                    </audio>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Button onClick={() => handleEditVoice(voice)} sx={{ mr: 1 }}>
                      {t('superAdminManageVoice.edit')}
                    </Button>
                    <Button color="error" onClick={() => openDeleteDialog(voice)}>
                      {t('superAdminManageVoice.delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {voiceSettings && (
          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
            <DialogTitle>
              {selectedVoice
                ? t('superAdminManageVoice.editTitle')
                : t('superAdminManageVoice.createTitle')}
            </DialogTitle>
            <DialogContent>
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.name')}
                fullWidth
                value={voiceSettings.name}
                onChange={(e) => setVoiceSettings({ ...voiceSettings, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.previewUrl')}
                fullWidth
                value={previewUrl}
                onChange={(e) => setPreviewUrl(e.target.value)}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.provider')}
                fullWidth
                value={voiceProvider}
                onChange={(e) => setVoiceProvider(e.target.value)}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.providerDisplayName')}
                fullWidth
                value={providerDisplayName}
                onChange={(e) => setProviderDisplayName(e.target.value)}
                helperText={t('superAdminManageVoice.form.providerDisplayNameHelper', {
                  provider: voiceProvider || (t('superAdminManageVoice.form.provider') as string),
                })}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.voiceId')}
                fullWidth
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.gender')}
                fullWidth
                select
                value={gender || ''}
                onChange={(e) => setGender(e.target.value)}>
                <MenuItem value="">{t('superAdminManageVoice.form.none')}</MenuItem>
                <MenuItem value="male">{t('superAdminManageVoice.form.male')}</MenuItem>
                <MenuItem value="female">{t('superAdminManageVoice.form.female')}</MenuItem>
              </TextField>
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.accent')}
                fullWidth
                value={accent || ''}
                onChange={(e) => setAccent(e.target.value)}
              />
              <TextField
                margin="dense"
                label={t('superAdminManageVoice.form.voiceSettings')}
                fullWidth
                multiline
                rows={16}
                value={voiceSettingsString}
                onChange={handleJsonChange}
                error={!isJsonValid}
                helperText={!isJsonValid && t('superAdminManageVoice.form.invalidJson')}
                InputProps={{
                  style: { fontFamily: 'Monaco, Menlo, monospace', fontSize: 13 },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} color="primary" disabled={!isJsonValid}>
                {t('common.save')}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Delete confirmation dialog */}
        <Dialog open={deleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('superAdminManageVoice.confirmDeleteTitle')}</DialogTitle>
          <DialogContent>
            <Typography>{t('superAdminManageVoice.deleteInstruction')}</Typography>
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Monaco, Menlo, monospace' }}>
                {voiceToDelete
                  ? t('superAdminManageVoice.deleteConfirmationPhrase', {
                      name: voiceToDelete.name,
                    })
                  : ''}
              </Typography>
            </Box>
            <TextField
              margin="dense"
              label={t('superAdminManageVoice.typeConfirmationLabel')}
              fullWidth
              value={deleteConfirmationInput}
              onChange={(e) => setDeleteConfirmationInput(e.target.value)}
              onPaste={(e) => {
                // Disable pasting into the confirmation field to force manual typing
                e.preventDefault();
              }}
              onDrop={(e) => {
                // Disable dropping text into the field
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                // Prevent Cmd/Ctrl+V paste via keyboard shortcuts
                if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
                  e.preventDefault();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog}>{t('common.cancel')}</Button>
            <Button
              color="error"
              onClick={handleDeleteConfirmed}
              disabled={
                !(
                  voiceToDelete &&
                  deleteConfirmationInput.trim() ===
                    t('superAdminManageVoice.deleteConfirmationPhrase', {
                      name: voiceToDelete.name,
                    })
                )
              }>
              {t('superAdminManageVoice.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
};

export default ManageVoiceSettings;
